// ============================================================================
//  AsyncCombobox — sélecteur avec recherche côté serveur (pattern ARIA 1.2
//  « combobox + listbox », focus conservé sur l'input, option active portée par
//  aria-activedescendant). Remplace les <select> tronqués à une page de résultats.
// ============================================================================
import * as React from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { Check, ChevronDown, Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks'

export interface AsyncComboboxProps<T> {
  value: string | null
  onChange: (id: string | null, item?: T) => void
  /** Charge une page d'options ; `limit` est fourni pour éviter de le dupliquer. */
  fetchOptions: (search: string, limit: number) => Promise<{ items: T[]; total?: number }>
  getOptionValue: (item: T) => string
  getOptionLabel: (item: T) => string
  /** Ligne secondaire sous le libellé (ville, email…). */
  renderOption?: (item: T) => React.ReactNode
  /** Libellé de la valeur courante lorsqu'elle n'est pas dans la page chargée. */
  initialLabel?: string
  label?: string
  /** Nom accessible quand le libellé visuel est rendu hors du composant. */
  ariaLabel?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  queryKey: string
  limit?: number
  debounceMs?: number
  allowClear?: boolean
  emptyText?: string
  id?: string
  /** Emplacement réservé (ex. création rapide), affiché en pied de liste. */
  footer?: React.ReactNode
}

type Selection = { id: string; label: string }

export function AsyncCombobox<T>({
  value, onChange, fetchOptions, getOptionValue, getOptionLabel, renderOption,
  initialLabel, label, ariaLabel, placeholder = '— Aucun —', required, disabled,
  queryKey, limit = 20, debounceMs = 250, allowClear = true, emptyText, id, footer,
}: AsyncComboboxProps<T>) {
  const baseId = React.useId()
  const inputId = id ?? `${baseId}-input`
  const listboxId = `${baseId}-listbox`
  const optionId = (i: number) => `${baseId}-opt-${i}`

  const rootRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)
  const openedByKeyboard = React.useRef(false)
  const scrolledOnOpen = React.useRef(false)

  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [activeIndex, setActiveIndex] = React.useState(-1)
  const [selected, setSelected] = React.useState<Selection | null>(
    value ? { id: value, label: initialLabel ?? '' } : null,
  )
  const debouncedSearch = useDebounce(search.trim(), debounceMs)

  // Synchronise la sélection interne avec la valeur contrôlée par le parent.
  React.useEffect(() => {
    setSelected((prev) => {
      if (!value) return null
      if (prev?.id === value) return prev.label || !initialLabel ? prev : { id: value, label: initialLabel }
      return { id: value, label: initialLabel ?? '' }
    })
  }, [value, initialLabel])

  const query = useQuery({
    queryKey: [queryKey, '__combobox', debouncedSearch, limit],
    queryFn: () => fetchOptions(debouncedSearch, limit),
    // Une requête par pause de frappe : rien n'est envoyé tant que la saisie évolue.
    enabled: open && !disabled && search.trim() === debouncedSearch,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
  const items = query.data?.items ?? []
  const total = query.data?.total
  const isStale = query.isPlaceholderData || search.trim() !== debouncedSearch
  const selectedIndex = selected ? items.findIndex((it) => getOptionValue(it) === selected.id) : -1

  // Nouvelle page de résultats : positionner l'option active, compléter le libellé manquant.
  React.useEffect(() => {
    if (!open || !query.data || query.isPlaceholderData) return
    setActiveIndex(!items.length ? -1 : selectedIndex >= 0 ? selectedIndex : (debouncedSearch || openedByKeyboard.current) ? 0 : -1)
    if (selected && !selected.label && selectedIndex >= 0) {
      setSelected({ id: selected.id, label: getOptionLabel(items[selectedIndex]) })
    }
    // Défilement unique à l'ouverture (première page reçue) : l'input d'abord, puis le panneau.
    if (!scrolledOnOpen.current) {
      scrolledOnOpen.current = true
      inputRef.current?.scrollIntoView({ block: 'nearest' })
      panelRef.current?.scrollIntoView({ block: 'nearest' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query.data, query.isPlaceholderData])

  React.useEffect(() => {
    if (open && activeIndex >= 0) {
      document.getElementById(`${baseId}-opt-${activeIndex}`)?.scrollIntoView({ block: 'nearest' })
    }
  }, [open, activeIndex, baseId])

  const openList = (byKeyboard = false) => {
    if (disabled || open) return
    openedByKeyboard.current = byKeyboard
    setSearch('')
    setActiveIndex(-1)
    setOpen(true)
  }
  const closeList = React.useCallback(() => {
    scrolledOnOpen.current = false
    setOpen(false)
    setSearch('')
    setActiveIndex(-1)
  }, [])

  // Clic hors du composant → fermeture sans sélection.
  React.useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) closeList()
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open, closeList])

  const select = (item: T) => {
    const id = getOptionValue(item)
    setSelected({ id, label: getOptionLabel(item) })
    onChange(id, item)
    closeList()
  }
  const clear = () => {
    setSelected(null)
    onChange(null)
    inputRef.current?.focus()
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) return openList(true)
        if (items.length) setActiveIndex((i) => Math.min(i + 1, items.length - 1))
        return
      case 'ArrowUp':
        e.preventDefault()
        if (open && items.length) setActiveIndex((i) => Math.max(i - 1, 0))
        return
      case 'Enter':
        if (!open) return
        e.preventDefault()
        if (query.isError) void query.refetch()
        else if (!isStale && activeIndex >= 0 && items[activeIndex]) select(items[activeIndex])
        return
      case 'Escape':
        if (!open) return
        // Ne ferme que la liste : la modale parente écoute aussi Escape.
        e.preventDefault()
        e.stopPropagation()
        closeList()
        return
      case 'Tab':
        if (open) closeList()
        return
      case 'Backspace':
        if (open && search === '' && selected && allowClear) {
          e.preventDefault()
          clear()
        }
        return
    }
  }

  const showClear = allowClear && !!selected && !disabled
  const hasMore = total != null && total > items.length
  const status = !open || isStale ? ''
    : query.isError ? 'Impossible de charger la liste.'
    : !items.length ? 'Aucun résultat.'
    : hasMore ? `${items.length} résultats affichés sur ${total}.`
    : `${items.length} résultat${items.length > 1 ? 's' : ''}.`

  return (
    <div ref={rootRef} className="relative w-full">
      {label && (
        <label htmlFor={inputId} className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
          {label}{required && <span className="text-[var(--red)] ml-1" aria-hidden="true">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-label={label ? undefined : ariaLabel}
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={open && activeIndex >= 0 && activeIndex < items.length ? optionId(activeIndex) : undefined}
          aria-required={required || undefined}
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          value={open ? search : selected?.label || (selected ? 'Sélection actuelle' : '')}
          placeholder={open ? 'Rechercher…' : placeholder}
          onChange={(e) => {
            if (!open) { openedByKeyboard.current = false; setOpen(true) }
            setSearch(e.target.value)
            setActiveIndex(-1)
          }}
          onFocus={(e) => { if (!open) e.currentTarget.select() }}
          onClick={() => openList()}
          onKeyDown={onKeyDown}
          onBlur={(e) => { if (!rootRef.current?.contains(e.relatedTarget as Node)) closeList() }}
          className={cn(
            'w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] truncate',
            'focus:outline-none focus:ring-1 focus:ring-[var(--blue)] placeholder:text-[var(--tx3)]',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            showClear ? 'pr-14' : 'pr-8',
          )}
        />
        {showClear && (
          <button
            type="button"
            aria-label="Effacer la sélection"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clear}
            className="absolute inset-y-0 right-7 my-auto flex h-7 w-7 items-center justify-center rounded-md text-[var(--tx3)] hover:text-[var(--brand-dark)] hover:bg-[var(--surface-3)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--blue)]"
          >
            <X className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        )}
        <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-[var(--tx3)]" aria-hidden="true">
          {open && query.isFetching
            ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--blue)]" />
            : <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', open && 'rotate-180')} />}
        </span>
      </div>

      <span role="status" aria-live="polite" className="sr-only">{status}</span>

      {open && (
        <div
          ref={panelRef}
          // Garde le focus sur l'input pour tout clic dans le panneau (pied, message, défilement).
          onMouseDown={(e) => e.preventDefault()}
          className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-[var(--bd)] rounded-lg shadow-lg overflow-hidden"
        >
          <ul id={listboxId} role="listbox" aria-label={label ?? ariaLabel} className={cn('max-h-[min(14rem,40vh)] overflow-y-auto', items.length > 0 && 'py-1', isStale && 'opacity-60')}>
            {items.map((item, i) => {
              const isSel = selected?.id === getOptionValue(item)
              return (
                <li
                  key={getOptionValue(item)}
                  id={optionId(i)}
                  role="option"
                  aria-selected={isSel}
                  onClick={() => select(item)}
                  onMouseMove={() => { if (activeIndex !== i) setActiveIndex(i) }}
                  className={cn(
                    'flex min-h-[44px] cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
                    i === activeIndex && 'bg-[var(--mauve-bg)]',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className={cn('block truncate', isSel ? 'font-semibold text-[var(--blue)]' : 'text-[var(--brand-dark)]')}>
                      {getOptionLabel(item)}
                    </span>
                    {renderOption && (
                      <span className="block truncate text-xs text-[var(--tx3)]">{renderOption(item)}</span>
                    )}
                  </span>
                  {isSel && <Check className="w-3.5 h-3.5 shrink-0 text-[var(--blue)]" aria-hidden="true" />}
                </li>
              )
            })}
          </ul>

          {query.isError ? (
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 text-xs text-[var(--red)]">
              <span>Impossible de charger la liste.</span>
              <button
                type="button"
                onClick={() => void query.refetch()}
                className="rounded-md px-2 py-1 font-medium text-[var(--blue)] hover:bg-[var(--mauve-bg)]"
              >
                Réessayer
              </button>
            </div>
          ) : !query.data ? (
            <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-[var(--tx3)]">
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Chargement…
            </div>
          ) : !items.length && !isStale ? (
            <p className="px-3 py-2.5 text-xs text-[var(--tx3)]">
              {debouncedSearch ? `Aucun résultat pour « ${debouncedSearch} »` : emptyText ?? 'Aucun élément'}
            </p>
          ) : hasMore ? (
            <p className="border-t border-[var(--bd)] bg-[var(--surface-2)] px-3 py-1.5 text-[11px] text-[var(--tx3)]">
              {items.length} sur {total} — affinez la recherche
            </p>
          ) : null}
          {footer}
        </div>
      )}
    </div>
  )
}
